import { getCookieValue, removeSSOToken } from "../utils/cookies";
import { BACKEND_BASE_URL } from "./Resolver";

export enum UserRole {
  ADMIN = "admin",
  OPERATOR = "operator",
  GUEST = "guest",
}

const LOGIN_URL = new URL(`${BACKEND_BASE_URL}/api/login?app=react`);

export class User {
  _token: string;
  id: number;
  role: UserRole;
  first_name: string;
  last_name: string;
  exp: number;

  constructor(token: string, id: number, role: UserRole, first_name: string, last_name: string, exp: number) {
    this._token = token;
    this.id = id;
    this.role = role;
    this.first_name = first_name;
    this.last_name = last_name;
    this.exp = exp;
  }

  private static isExpired = (exp: number) => Date.now() >= exp * 1000;

  public static redirectToLoginPage(): void {
    window.location.assign(LOGIN_URL);
    throw new Error("Unable to get a valid token, redirected to login page");
  }

  public static fromJWT(token: string | null): User {
    if (!token) this.redirectToLoginPage();
    let jsonPayload: any = null;

    try {
      const base64Url = token!.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
    } catch (_) {
      // Refresh token if we can't decode it.
      this.redirectToLoginPage();
    }

    const decoded = JSON.parse(jsonPayload);
    if (!decoded.role || !decoded.externalID || !decoded.first_name || !decoded.last_name || !decoded.exp) {
      this.redirectToLoginPage();
    }

    return new User(
      token!,
      Number(decoded.externalID),
      decoded.role as UserRole,
      decoded.first_name,
      decoded.last_name,
      decoded.exp,
    );
  }

  public getValidToken(): string {
    if (User.isExpired(this.exp)) User.redirectToLoginPage();
    return this._token;
  }

  public logout(): void {
    this._token = "";
    this.id = -1;
    this.role = UserRole.GUEST;
    this.first_name = "";
    this.last_name = "";
    this.exp = -1;
    removeSSOToken();
  }
}

// This user instance is used across the app.
// Will be static until app reload event (page refresh).
// To avoid SSO auth during local development define REACT_APP_LOCAL_USER_TOKEN env.
// It is also important for unit tests and Storybook UI tests.
export const currentUser = User.fromJWT(process.env.REACT_APP_LOCAL_USER_TOKEN || getCookieValue("token"));
